/**
 * Self-registration form hook for Aegix One.
 *
 * ADR-118 #18: wraps the lib's `useAuthActions().signup`, which returns
 * `LoginResult | RequiresVerification` (discriminated via
 * `isRequiresVerification` from `@aegix/auth-identity`).
 *
 * When the adapter auto-logs the user in (signup response carries a
 * `data.user`), the provider has already dispatched `login_success`
 * by the time this hook resolves — no extra step needed; App.tsx
 * re-renders into the authenticated routes.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useAuthActions,
  isRequiresVerification,
  type AllauthUser,
} from '@aegix/auth-identity';

// Sentinel for the adapter-lost-signup invariant below. The throw stays
// untranslated so error reporting would fingerprint one group regardless of
// the user's language; the catch maps it to the localized message at the
// setError boundary, consistent with the other auth hooks.
const SIGNUP_UNAVAILABLE = 'signup_unavailable';

export function useRegisterForm() {
  const { t } = useTranslation();
  const actions = useAuthActions<AllauthUser>();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (values: {
    email: string;
    password: string;
    firstName?: string;
    lastName?: string;
  }) => {
    setError('');
    setLoading(true);
    try {
      if (!actions.signup) {
        // The provider only exposes `signup` when the adapter
        // implements it. allauthAdapter does — this branch is
        // defensive against a future adapter swap that drops it.
        throw new Error(SIGNUP_UNAVAILABLE);
      }
      const result = await actions.signup({
        email: values.email,
        password: values.password,
        first_name: values.firstName || undefined,
        last_name: values.lastName || undefined,
      });
      if (isRequiresVerification(result)) {
        navigate('/verify-email', { state: { email: values.email } });
      }
      // No verification needed — provider has dispatched login_success
      // and App.tsx will redirect to the dashboard.
    } catch (err) {
      if (err instanceof Error && err.message === SIGNUP_UNAVAILABLE) {
        setError(t('auth.signupUnavailable'));
      } else {
        setError(
          err instanceof Error
            ? err.message
            : t('auth.registrationFailed'),
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return { error, setError, loading, showPassword, setShowPassword, handleSubmit };
}
